import {
  MAX_ITEMS,
  PERFORMANCE_ID_PATTERN,
  PERFORMANCE_ID_PREFIX,
} from './constants'

/**
 * Lógica del historial de "vistos recientemente": funciones **puras** (sin
 * `document` ni React) para que el formato, el orden, la deduplicación y el
 * tope sean idénticos al leer y al escribir, y testeables en aislamiento
 * (specs/024-vistos-recientemente, research §8).
 *
 * El contrato del valor de la cookie está en
 * `specs/024-vistos-recientemente/contracts/recently-viewed-cookie.md`.
 */

/** `decodeURIComponent` lanza ante una secuencia `%` mal formada; un valor de
 * cookie corrupto no debe romper la página, se trata como historial vacío. */
function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return ''
  }
}

/**
 * Valor de la cookie → lista de ids canónicos.
 *
 * Descodifica primero (`setCookie` codifica al escribir, así que la coma puede
 * llegar como `%2C`), trocea por comas, recorta espacios y descarta lo que no
 * sea un id de evento válido: el valor es entrada de frontera manipulable por
 * el visitante. Deduplica conservando la primera aparición y recorta al tope,
 * de modo que una cookie sembrada desde fuera con más de `MAX_ITEMS` ids nunca
 * produzca un historial más largo del permitido (FR-001a, FR-008, FR-036).
 *
 * Un valor en otro formato (p. ej. JSON) no encaja con el patrón de id y
 * termina en lista vacía, sin error: el bloque simplemente no se muestra.
 */
export function parseHistory(value: string | undefined | null): readonly string[] {
  if (!value) return []

  const seen = new Set<string>()

  for (const segment of safeDecode(value).split(',')) {
    const id = segment.trim()
    if (!PERFORMANCE_ID_PATTERN.test(id)) continue
    seen.add(id)
    if (seen.size === MAX_ITEMS) break
  }

  return [...seen]
}

/**
 * Lista de ids → valor de la cookie (lista separada por comas).
 *
 * Recorta al tope aquí, y no solo en `addToHistory`, para que **cualquier**
 * escritura lo respete: un historial sembrado desde fuera con 20 ids se queda
 * en 15 la primera vez que el sistema lo reescriba, p. ej. al quitar un evento
 * (FR-036).
 */
export function serializeHistory(ids: readonly string[]): string {
  return ids.slice(0, MAX_ITEMS).join(',')
}

/**
 * Id de evento en su forma canónica (`performance-<id>`), o `null` si no se
 * puede normalizar. Acepta el id "desnudo" que puede exponer una ficha
 * (`M01M01LMIS01`) y le añade el prefijo (FR-033).
 */
export function normalizePerformanceId(raw: string | null | undefined): string | null {
  if (!raw) return null

  const trimmed = raw.trim()
  if (!trimmed) return null

  if (trimmed.startsWith(PERFORMANCE_ID_PREFIX)) {
    return PERFORMANCE_ID_PATTERN.test(trimmed) ? trimmed : null
  }

  // Un id "desnudo" solo puede ser alfanumérico (`M01M01LMIS01`,
  // `15076150764902`). Si trae un guion, ya lleva prefijo de otro tipo de
  // recurso (`venue-4902`, `event-…`): añadirle `performance-` fabricaría un id
  // inexistente que ensuciaría el historial y gastaría una plaza del tope.
  if (!/^[A-Za-z0-9]+$/.test(trimmed)) return null

  return `${PERFORMANCE_ID_PREFIX}${trimmed}`
}

/**
 * Registra un evento: entra en primera posición (más reciente primero). Si ya
 * estaba, **sube** al principio en vez de duplicarse, conservando el orden
 * relativo del resto. Se recorta al tope descartando los más antiguos
 * (FR-034, FR-035, FR-036).
 *
 * Un id no normalizable deja el historial intacto (FR-033).
 */
export function addToHistory(
  ids: readonly string[],
  rawId: string | null | undefined,
): readonly string[] {
  const id = normalizePerformanceId(rawId)
  if (!id) return ids

  return [id, ...ids.filter((current) => current !== id)].slice(0, MAX_ITEMS)
}

/** Da de baja un evento conservando el orden del resto (FR-018). */
export function removeFromHistory(
  ids: readonly string[],
  id: string,
): readonly string[] {
  return ids.filter((current) => current !== id)
}
